/**
 * A headline number with a label and a line under it — the tiles at the top
 * of the overview, the console and a customer's page.
 *
 * One component rather than three copies, so the tiles look like the same
 * object everywhere and a change to how a number is shown happens once. When
 * `to` is given the whole tile is a link.
 */

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

export type MetricTone = 'ok' | 'warn' | 'bad'

const TONE_COLOR: Record<MetricTone, string> = {
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  bad: 'var(--danger)',
}

export default function Metric({
  label,
  value,
  hint,
  to,
  icon,
  tone,
  emphasis,
}: {
  label: string
  value: ReactNode
  hint?: ReactNode
  to?: string
  icon?: ReactNode
  /** Colours the hint and the tile's edge — not the number, which stays
   *  legible as a figure rather than becoming a warning light. */
  tone?: MetricTone
  /** Colour the value itself in the brand green — the one figure on a page
   *  that is the reason the page exists, like a balance. */
  emphasis?: boolean
}) {
  const body = (
    <>
      <div className="metric-top">
        <span className="stat-label">{label}</span>
        {icon && <span className="metric-chip">{icon}</span>}
      </div>
      <span
        className="stat-value"
        style={emphasis ? { color: 'var(--brand-text)' } : undefined}
        title={typeof value === 'string' ? value : undefined}
      >
        {value}
      </span>
      {hint && (
        <span className="tiny metric-hint" style={tone ? { color: TONE_COLOR[tone] } : undefined}>
          {hint}
        </span>
      )}
    </>
  )

  const style = tone ? ({ '--metric-edge': TONE_COLOR[tone] } as React.CSSProperties) : undefined

  return to ? (
    <Link className={`card card-link metric ${tone ? 'toned' : ''}`} to={to} style={style}>
      {body}
    </Link>
  ) : (
    <div className={`card metric ${tone ? 'toned' : ''}`} style={style}>
      {body}
    </div>
  )
}
